import { DestroyRef, inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Location } from '@angular/common';
import { filter, Subscription, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class BackHandlerService {
  // Injections
  private router = inject(Router);
  private location = inject(Location);
  private initialized = false;
  destroyRef = inject(DestroyRef);

  // Variables
  private customLink: string | undefined = undefined;
  private scrollableElement: HTMLElement | null = null;
  private scrollableElementIds: { order: number; id: string }[] = [
    { order: 0, id: 'dpx-main-layout-body' },
    { order: 1, id: 'dpx-page-layout-body' },
  ];
  private backNavigating = false;
  private scrollTopRepository: { [key: string]: number } = {};
  private skipDefer = false;
  private hasNavigated = false;
  private restrictedUrls: string[] = ['payment/result'];
  private redirectForRestrictedUrls = '/';
  private hasCustomLinkFromState = false;
  private routerEventsSub: Subscription = new Subscription();
  private popStateFun = () => {
    this.backNavigating = true;
    if (this.isUserFromAnotherSource()) {
      this.addNullState();
      setTimeout(() => {
        this.navigate('/');
      }, 0);
      return;
    }
    if (!this.customLink) {
      if (this.isNavBackBanned()) {
        this.addNullState();
        setTimeout(() => {
          this.navigate(this.redirectForRestrictedUrls);
        }, 0);
      }
      return;
    } else {
      setTimeout(() => {
        this.addNullState();
        this.navigate(this.customLink!).then(() => {
          this.customLink = undefined;
        });
      }, 0);
    }
  };
  navigateTimeOut: any;
  routerEventsTimeOut: any;
  restoreScrollTimeOut: any;

  /*
   * Public Methods
   * */
  public init(): void {
    /**
     * WARNING: It has to ba called ONLY ONCE on the main component of project
     **/
    if (!this.initialized) {
      const routerSub = this.router.events
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          filter((event) => event instanceof NavigationEnd),
          take(1),
        )
        .subscribe({
          next: () => {
            this.initialized = true;
            routerSub.unsubscribe();
            this.addDefaultState();
            this.startListening();
          },
        });
    }
  }

  setCustomBackUrl(url: string): void {
    /**
     * WARNING: If you want this custom Url to work, user should preform an action on the page
     **/
    if (url && !this.hasCustomLinkFromState) {
      this.customLink = this.removeLeadingSlash(url);
    }
  }

  goBack(): void {
    this.location.back();
  }

  shouldSkipDeferLoading(url: string): boolean {
    this.skipDefer = this.findScrollTopInRepository(url) > 0;
    return this.skipDefer;
  }

  setRestrictedUrls(restrictedUrls: string[]): void {
    this.restrictedUrls = restrictedUrls;
  }

  setRedirectForRestrictedUrls(redirectUrl: string): void {
    this.redirectForRestrictedUrls = this.removeLeadingSlash(redirectUrl);
  }

  removeLeadingSlash(url: string): string {
    return url.startsWith('/') && url !== '/' ? url.substring(1) : url;
  }

  destroy(): void {
    if (this.routerEventsSub) {
      this.routerEventsSub.unsubscribe();
    }
    if (this.navigateTimeOut) {
      clearTimeout(this.navigateTimeOut);
    }
    if (this.routerEventsTimeOut) {
      clearTimeout(this.routerEventsTimeOut);
    }
    if (this.restoreScrollTimeOut) {
      clearTimeout(this.restoreScrollTimeOut);
    }
    window.removeEventListener('popstate', this.popStateFun);
  }

  /*
   * Private Methods
   * */
  private startListening(): void {
    window.addEventListener('popstate', this.popStateFun);
    this.routerEventsSub = this.router.events.subscribe({
      next: (e) => {
        this.findScrollableElement();
        if (e instanceof NavigationStart) {
          this.saveScrollTopInRepository(this.router.url, this.scrollableElement?.scrollTop ?? 0);
          this.hasNavigated = true;
          this.checkNavigationState();
        } else if (e instanceof NavigationEnd) {
          setTimeout(() => {
            if (this.hasNavigated && this.customLink && !this.hasCustomLinkFromState) {
              this.customLink = undefined;
            }
          }, 1);
          this.restoreScroll(this.router.url);
        }
      },
    });
  }

  private checkNavigationState(): void {
    /**
     * NOTE: If you want to redirect, but you want to have a custom back url in the destination, you should add customLinkForBack to the navigation state.
     * */
    const stateObj = this.router.getCurrentNavigation()?.extras?.state ?? {};
    const customLinkFromState: string = Object.prototype.hasOwnProperty.call(stateObj, 'customLinkForBack')
      ? this.router.getCurrentNavigation()?.extras?.state!['customLinkForBack']
      : '';
    this.hasCustomLinkFromState = customLinkFromState !== '';
    if (this.hasCustomLinkFromState) {
      this.customLink = this.removeLeadingSlash(customLinkFromState);
    }
  }

  private navigate(url: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.navigateTimeOut = setTimeout(() => {
        this.router.navigateByUrl(url, { replaceUrl: true }).then(resolve).catch(reject);
      }, 0);
    });
  }

  private saveScrollTopInRepository(url: string, scrollToTop: number): void {
    this.scrollTopRepository = {
      ...this.scrollTopRepository,
      [url]: scrollToTop,
    };
  }

  private findScrollTopInRepository(url: string): number {
    return Object.prototype.hasOwnProperty.call(this.scrollTopRepository, url) ? this.scrollTopRepository[url] : 0;
  }

  private findScrollableElement(): void {
    const idsArray = this.scrollableElementIds.sort((a, b) => b.order - a.order);
    for (const item of idsArray) {
      const element = document.getElementById(item.id);
      if (element) {
        this.scrollableElement = element;
        break;
      }
    }
  }

  private restoreScroll(url: string): void {
    this.restoreScrollTimeOut = setTimeout(() => {
      const scrollTop = this.backNavigating ? this.findScrollTopInRepository(url) : 0;
      this.findScrollableElement();
      const interval = setInterval(() => {
        if (this.scrollableElement && this.scrollableElement.scrollHeight >= scrollTop) {
          if (!this.backNavigating) {
            this.scrollableElement.style.scrollBehavior = 'smooth';
          }
          this.scrollableElement.scrollTop = scrollTop;
          clearInterval(interval);
        }
      }, 50); // Check every 100ms
      setTimeout(() => {
        clearInterval(interval);
      }, 2000);
    }, 0);
  }

  private addNullState(): void {
    window.history.pushState(null, '', this.router.url);
  }

  private addDefaultState(): void {
    window.history.pushState(null, '', '/');
    this.addNullState();
  }

  private isNavBackBanned(): boolean {
    return this.restrictedUrls.some((url) => this.removeLeadingSlash(url).startsWith(this.removeLeadingSlash(this.router.url)));
  }

  private isUserFromAnotherSource(): boolean {
    const previousUrl = document.referrer;
    if (this.hasNavigated) {
      return false;
    }
    if (previousUrl) {
      return !previousUrl.startsWith(window.location.origin);
    } else {
      return true;
    }
  }
}
